import React, {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import {change, initialize, reset, reduxForm} from 'redux-form';
import {connect} from 'react-redux';
import {compose} from 'recompose';
import JobBooleanInput from './JobBooleanInput';
import JobDateInput from './JobDateInput';
import JobDateFromToInput from './JobDateFromToInput';
import JobMonthDayInput from './JobMonthDayInput';
import JobNumberInput from './JobNumberInput';
import JobListInput from './JobListInput';
import JobSingleFromListInput from './JobSingleFromListInput';
import JobStringInput from './JobStringInput';
import ReportJobBuilderCategorySelect from './ReportJobBuilderCategorySelect';
import ReportJobBuilderReportSelect from './ReportJobBuilderReportSelect';
import {assembleSelectOptionsFromConfigData} from '../shared/reportJobFunctions';
import {REPORT_JOB_CUSTOM_TYPES} from '../shared/reportJobLists';
import {commonStyles} from '../shared/styles';
import {filterStyles} from '../shared/styles-filter';
import {jobBuilderStyles} from '../shared/styles-job-builder';
import CircularProgress from 'material-ui/CircularProgress';
import RaisedButton from 'material-ui/RaisedButton';
import ErrorIcon from 'material-ui/svg-icons/alert/error-outline';
import CategoryIcon from 'material-ui/svg-icons/action/description';

class JobReportBuilderForm extends Component {
    constructor(props) {
        super(props);
        const today = new Date();
        const year = today.getFullYear();
        let mm = today.getMonth() + 1;
        let dd = today.getDate();
        if (dd < 10) dd = '0' + dd;
        const todayStr = mm + '/' + dd + '/' + year;
        const system = encodeURIComponent(localStorage.getItem('system'));

        this.state = {
            clearForm: false,
            error: false,
            reportChoices: [],
            categoriesExpanded: true,
            message: null,
            reportParams: [],
            reportsExpanded: true,
            selectedCategory: {
                id: '',
                title: '',
                description: ''
            },
            selectedReport: {
                id: '',
                title: '',
                description: ''
            },
            submitting: false,
            system: system,
            today: todayStr
        };
    }

    componentWillReceiveProps(nextProps, nextContext) {
        if (!nextProps.submitting && this.state.submitting) {
            this.setState({
                submitting: false
            });
        }
    }

    // Clear form values and notify inputs to clear internal values
    clearForm = () => {
        this.props.dispatch(reset('jobBuilderForm'));
        this.setState({
            clearForm: true
        }, () => {
            this.setState({
                clearForm: false
            });
        });
        if (typeof this.props.onFormReset === 'function') {
            this.props.onFormReset();
        }
    };

    getFormValues = () => {
        return JSON.parse(JSON.stringify(this.props.formValues));
    }

    // Get default values from Report parameters
    getReportDefaults = (params) => {
        // Set System value if present
        const systemParam = params.find(r => r.name === 'system');
        const cat = this.state.selectedCategory.id;
        const method = this.state.selectedReport.id;
        const reportTitle = this.state.selectedReport.title + ' formatted on ' + this.state.today;

        let defaults = {
            category: cat,
            method: method,
            report_name: reportTitle,
            report_description: this.state.selectedReport.description
        };
        Object.keys(params).forEach(key => {
            const item = params[key];
            if (item.name === 'system') {
                defaults.system = (systemParam) ? this.state.system : undefined;
            } else {
                defaults[item.name] = item.default;
            }
        });
        return defaults;
    };

    // Set form values from Report defaults
    setReportDefaults = (params) => {
        const defaults = this.getReportDefaults(params);
        this.props.dispatch(initialize('jobBuilderForm', defaults, false));
    };

    // Event: Handle Report Category selection and assemble available Reports
    handleCategorySelect = (e) => {
        const config = this.props.configData;
        // Assemble Report options from selected Category
        const reportChoices = assembleSelectOptionsFromConfigData('executors', config[e].executors);

        this.setState({
            categoriesExpanded: false,
            reportChoices: (reportChoices && reportChoices.length > 0) ? reportChoices : [],
            reportsExpanded: true,
            selectedCategory: {
                id: e,
                title: (config[e]) ? config[e].title : '',
                description: (config[e]) ? config[e].description : ''
            },
            selectedReport: {
                id: '',
                title: '',
                description: ''
            }
        }, () => {
            this.clearForm();
        });
    };

    // Event: Handle Report selection and assemble Report params
    handleReportSelect = (e) => {
        const config = this.props.configData;
        const sp = this.state.selectedCategory;
        const executors = config[sp.id].executors;
        let params = (executors && executors[e].params) ? executors[e].params : [];
        let valid = true;
        let message = null;

        // If the selected Report contains an unrecognized Custom Type, do not display the form
        params.forEach((param) => {
            if (!REPORT_JOB_CUSTOM_TYPES.includes(param.custom_type)) {
                valid = false;
                message = 'Sorry, this Report is not yet supported. Please select a different Category or Report.';
            }
        });
        let title = (valid && executors) ? executors[e].title : '';
        let description = (valid && executors) ? executors[e].description : '';

        this.setState({
            error: !valid,
            message: message,
            reportParams: (params && valid) ? params : [],
            reportsExpanded: (!valid),
            selectedReport: {
                id: (valid) ? e : '',
                title: title,
                description: description
            }
        }, () => {
            this.clearForm();
            if (valid) {
                this.setReportDefaults(params);
                // Reset the title and description fields
                this.props.dispatch(change('jobBuilderForm', 'report_name', title + ' formatted on ' + this.state.today));
                this.props.dispatch(change('jobBuilderForm', 'report_description', description));
            }
        });
    };

    handleFieldUpdate = (type, name, value) => {
        this.props.dispatch(change('jobBuilderForm', name, value));
    };

    validateForm = () => {
        if (this.props && this.props.formValues) {
            const values = this.getFormValues();
            const params = JSON.parse(JSON.stringify(this.state.reportParams));
            if (!values) {
                return false;
            }
            if (values) {
                if (!values.report_name || values.report_name === '') {
                    return false;
                }
                for (let v = 0; v < params.length; v++) {
                    const item = params[v];
                    const customType = item.custom_type;
                    const nativeType = item.native_type;
                    const isRequired = item.required;
                    const value = values[item.name];
                    if (isRequired) {
                        if (!value && nativeType !== 'bool') {
                            return false;
                        }
                        if (
                            (
                                nativeType === 'str' ||
                                nativeType === 'City' ||
                                nativeType === 'DateString' ||
                                nativeType === 'DBA' ||
                                nativeType === 'District' ||
                                nativeType === 'ProductType' ||
                                nativeType === 'Location' ||
                                nativeType === 'LocationType' ||
                                nativeType === 'State' ||
                                nativeType === 'TextArea' ||
                                item.name === 'report_name'
                            )
                            && value === ''
                        ) {
                            return false;
                        }
                        if (nativeType === 'list' && value.length === 0) {
                            return false;
                        }
                        if (
                            (
                                customType === 'DateFromTo'
                            )
                            && (!value[0] || !value[1])) {
                            return false;
                        }
                    }
                    if (
                        (
                            customType === 'DateFromTo'
                        )
                        && !value[0]) {
                        return false;
                    }
                }
            }
        }

        return true;
    };

    handleSubmit = () => {
        const params = JSON.parse(JSON.stringify(this.state.reportParams));
        const fv = this.getFormValues();
        let payload = {
            name: fv.report_name,
            description: (fv.report_description) ? fv.report_description : '',
            package: this.state.selectedCategory.id,
            class: this.state.selectedReport.id,
            args: {
                system: fv.system,
            }
        };
        params.forEach((param) => {
            const type = param.custom_type;
            let empty = '';
            if (type === 'bool') {
                empty = false;
            }
            if (type === 'list') {
                empty = [];
            }
            const paramVal = fv[param.param];
            payload.args[param.param] = (paramVal) ? paramVal : empty;
        });

        this.setState({
            submitting: true
        });
        if (typeof this.props.onFormComplete === 'function') {
            this.props.onFormComplete(payload);
        }
    };

    // Render inputs for each Param in the selected Report
    renderReportParams = (list) => {
        if (!list || list.length === 0) {
            return (
                [0].map(() => (
                    <span key="job-report-param-0"></span>
                ))
            )
        }
        return list.map((item, idx) => {
            const reset = this.state.clearForm;
            const type = item.custom_type;
            const key = "job-report-param-" + type + "-" + item.param;

            switch (type) {
                case 'bool':
                    return <JobBooleanInput
                        key={key}
                        customType={type}
                        param={item.name}
                        name={item.name}
                        label={item.label}
                        tooltip={item.tooltip}
                        defaultValue={(item.default) ? item.default : false}
                        onChange={this.handleFieldUpdate}
                        reset={reset}
                    />
                case 'int':
                case 'float':
                    return <JobNumberInput
                        key={key}
                        customType={type}
                        isFloat={(type === 'float')}
                        param={item.name}
                        name={item.name}
                        label={item.label}
                        tooltip={item.tooltip}
                        defaultValue={item.default || undefined}
                        required={(item.required) ? true : undefined}
                        onChange={this.handleFieldUpdate}
                        style={filterStyles.selectInputFull}
                        reset={reset}
                    />
                case 'str':
                    return <JobStringInput
                        key={key}
                        customType={type}
                        param={item.name}
                        name={item.name}
                        label={item.label}
                        tooltip={item.tooltip}
                        defaultValue={item.default || undefined}
                        required={(item.required) ? true : undefined}
                        onChange={this.handleFieldUpdate}
                        style={filterStyles.selectInputFull}
                        reset={reset}
                    />
                case 'System':
                    return <Fragment key={`job-report-param-System`}></Fragment>
                case 'DateString':
                    return <JobDateInput
                        key={key}
                        customType={type}
                        param={item.name}
                        name={item.name}
                        label={item.label}
                        tooltip={item.tooltip}
                        defaultValue={(item.default !== '') ? item.default : undefined}
                        nativeType={item.nativeType}
                        required={(item.required) ? true : undefined}
                        onSelectDate={this.handleFieldUpdate}
                        reset={reset}
                    />
                case 'DateFromTo':
                    return <JobDateFromToInput
                        key={key}
                        customType={type}
                        param={item.name}
                        name={item.name}
                        label={item.label}
                        tooltip={item.tooltip}
                        nativeType={item.nativeType}
                        required={(item.required) ? true : undefined}
                        onSelectDate={this.handleFieldUpdate}
                        reset={reset}
                    />
                case 'Day':
                    return <JobNumberInput
                        key={key}
                        customType={type}
                        isFloat={false}
                        param={item.name}
                        name={item.name}
                        label={item.label}
                        tooltip={item.tooltip}
                        defaultValue={item.default || undefined}
                        max={31}
                        min={1}
                        required={(item.required) ? true : undefined}
                        onChange={this.handleFieldUpdate}
                        style={filterStyles.selectInputFull}
                        reset={reset}
                    />
                case 'MonthDay':
                    return <JobMonthDayInput
                        key={key}
                        customType={type}
                        param={item.name}
                        name={item.name}
                        label={item.label}
                        tooltip={item.tooltip}
                        defaultValue={item.default || ''}
                        nativeType={item.native_type}
                        returnKey="id"
                        required={(item.required) ? true : undefined}
                        onItemSelect={this.handleFieldUpdate}
                        reset={reset}
                    />
                case 'Cities':
                case 'DBAs':
                case 'Districts':
                case 'Locations':
                case 'LocationTypes':
                case 'ProductTypes':
                case 'States':
                    return <JobListInput
                        key={key}
                        customType={type}
                        param={item.name}
                        name={item.name}
                        label={item.label}
                        tooltip={item.tooltip}
                        defaultValue={item.default || []}
                        nativeType={item.native_type}
                        required={(item.required) ? true : undefined}
                        onListUpdate={this.handleFieldUpdate}
                        reset={reset}
                    />
                case 'DBA':
                case 'District':
                case 'Location':
                case 'LocationType':
                case 'Month':
                case 'ProductType':
                case 'State':
                case 'Year':
                    return <JobSingleFromListInput
                        key={key}
                        customType={type}
                        param={item.name}
                        name={item.name}
                        label={item.label}
                        tooltip={item.tooltip}
                        defaultValue={item.default || ''}
                        nativeType={item.native_type}
                        returnKey={(type === 'ProductType') ? 'name' : 'id'}
                        required={(item.required) ? true : undefined}
                        onItemSelect={this.handleFieldUpdate}
                        reset={reset}
                    />
                case 'TextArea':
                    return <JobStringInput
                        key={key}
                        multiline={true}
                        rows={4}
                        customType={type}
                        param={item.name}
                        name={item.name}
                        label={item.label}
                        tooltip={item.tooltip}
                        defaultValue={item.default || undefined}
                        required={(item.required) ? true : undefined}
                        onChange={this.handleFieldUpdate}
                        style={filterStyles.selectInputMultilineFull}
                        reset={reset}
                    />
                default:
                    break;
            }

            return (
                <div key={`job-report-param-${item.param}`}>(INPUT: {item.param})</div>
            )
        });
    };

    render() {
        const {
            configData,
            formValues,
            submitted,
            success
        } = this.props;

        const {
            error,
            message,
            reportChoices,
            reportParams,
            categoriesExpanded,
            reportsExpanded,
            selectedCategory,
            selectedReport,
            reset,
            submitting,
            today
        } = this.state;

        let reportTitle = selectedReport.title + ' formatted on ' + today;
        if (formValues && formValues.report_name !== undefined) {
            reportTitle = formValues.report_name;
        }

        let reportDesc = selectedReport.description;
        if (formValues && formValues.report_description !== undefined) {
            reportDesc = formValues.report_description;
        }

        return (
            <form>
                <div className="list-filter">
                    {configData &&
                        <Fragment>
                            <ReportJobBuilderCategorySelect
                                expanded={categoriesExpanded}
                                configData={configData}
                                selected={(selectedCategory) ? selectedCategory.id : ''}
                                onSelectCategory={(e) => this.handleCategorySelect(e)}
                            />

                            {selectedCategory.id &&
                                <Fragment>
                                    {reportChoices.length > 0 &&
                                        <ReportJobBuilderReportSelect
                                            expanded={reportsExpanded}
                                            configData={configData}
                                            reportChoices={reportChoices}
                                            selected={(selectedReport) ? selectedReport.id : ''}
                                            onSelectReport={(e) => this.handleReportSelect(e)}
                                        />
                                    }
                                    {(!reportChoices || !reportChoices.length) &&
                                        <p>
                                            The {selectedCategory.title} Category has no Reports. Please select a
                                            different option.
                                        </p>
                                    }
                                    {selectedReport.id &&
                                        <Fragment>
                                            <div className="reports-table-title table-table-header">
                                                <h3 className="table-table-header-title">
                                                    <CategoryIcon style={commonStyles.summaryIcon}/>
                                                    {selectedReport.id &&
                                                        <span>{selectedReport.title}</span>
                                                    }
                                                </h3>
                                            </div>

                                            {[...this.renderReportParams(reportParams)]}

                                            <JobStringInput
                                                customType="str"
                                                param="report_name"
                                                name="report_name"
                                                label="Name of Report"
                                                tooltip="Create a name for this Report."
                                                allowOverride={true}
                                                value={reportTitle}
                                                required={true}
                                                onChange={this.handleFieldUpdate}
                                                style={filterStyles.selectInputFull}
                                                reset={reset}
                                            />

                                            <JobStringInput
                                                customType="str"
                                                param="report_description"
                                                name="report_description"
                                                label="Report Description"
                                                tooltip="Create a description for this Report. This will help locate the Report in the future. You may wish to include information such as who the Report is intended for, or how it is intended to be used."
                                                multiline={true}
                                                rows={3}
                                                allowOverride={true}
                                                value={reportDesc}
                                                onChange={this.handleFieldUpdate}
                                                style={filterStyles.selectInputMultilineFull}
                                                reset={reset}
                                            />

                                            <hr style={commonStyles.divider}/>
                                            <br/>
                                            <RaisedButton
                                                primary
                                                type="button"
                                                label="Build Report Job"
                                                style={commonStyles.buttonInline}
                                                disabled={(this.validateForm() === false || submitting || success)}
                                                onClick={() => this.handleSubmit()}
                                            />
                                            <RaisedButton
                                                type="button"
                                                label="Cancel"
                                                href="/#/ReportJobs"
                                                disabled={(submitting || success)}
                                                style={commonStyles.buttonInline}
                                            />

                                        </Fragment>
                                    }
                                </Fragment>
                            }
                        </Fragment>
                    }
                    <hr style={filterStyles.break}/>
                    {submitting &&
                        <div style={jobBuilderStyles.message}>
                            <CircularProgress
                                size={24}
                                thickness={2}
                                className="loader"
                            />
                            <p style={jobBuilderStyles.loading}>
                                Submitting...
                            </p>
                        </div>
                    }
                    {submitted && !submitting && !success &&
                        <div style={jobBuilderStyles.message}>
                            <p style={jobBuilderStyles.error}>
                                <ErrorIcon style={jobBuilderStyles.messageIcon}/>
                                Sorry, there was an issue creating Report Job "{reportTitle}". Please
                                review the form and try again.
                            </p>
                        </div>
                    }
                    {submitted && !submitting && success &&
                        <div style={jobBuilderStyles.message}>
                            <p style={jobBuilderStyles.messageP}>
                                {/* No message; redirect will occur*/}
                            </p>
                        </div>
                    }
                    {message &&
                        <div style={jobBuilderStyles.message}>
                            <p style={(error) ? jobBuilderStyles.error : jobBuilderStyles.messageP}>
                                {message}
                            </p>
                        </div>
                    }
                </div>
            </form>
        )
    }
}

const mapStateToProps = (state) => {
    let form = state.form['jobBuilderForm'];
    if (form && form.values) {
        return {
            formValues: form.values
        }
    }
    return {}
};

JobReportBuilderForm.propTypes = {
    configData: PropTypes.object,
    onFormComplete: PropTypes.func,
    onFormReset: PropTypes.func,
    submitted: PropTypes.bool,
    submitting: PropTypes.bool,
    success: PropTypes.bool
};

JobReportBuilderForm.defaultProps = {
    configData: null,
    submitted: false,
    submitting: false,
    success: false
};

const enhance = compose(
    connect(mapStateToProps),
    reduxForm({
        form: 'jobBuilderForm',
        enableReinitialize: true,
        initialValues: {}
    })
);

export default enhance(JobReportBuilderForm);



// WEBPACK FOOTER //
// ./src/Fuse/components/ReportJobBuilderForm.js